import logo from '../assets/logos/logo.png';
import bgLogin from '../assets/images/bg-login.webp';

export interface ThemeTokens {
  name: string;
  colors: {
    primary: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    secondary: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    error: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    warning: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    info: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    success: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    background: {
      default: string;
      paper: string;
    };
    text: {
      primary: string;
      secondary: string;
    };
  };
  customPalette: {
    gradients: {
      dark: {
        main: string;
        state: string;
      };
    };
    coloredShadows: {
      primary: string;
      secondary: string;
      info: string;
      success: string;
      warning: string;
      error: string;
      light: string;
      dark: string;
    };
    sidebar: {
      background: string;
      backgroundDark: string;
      text: string;
      textSecondary: string;
      hover: string;
      active: string;
      border: string;
    };
  };
  spacing: number;
  typography: {
    fontFamily: string;
    fontSize: number;
  };
}

export interface BrandingConfig {
  title: string;
  tagline: string;
  description: string;
  metaDescription: string;
  companyInfo: {
    legalName: string;
    phone: string;
    email: string;
    address: string;
  };
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    youtube?: string;
    linkedin?: string;
    twitter?: string;
  };
  copyright: string;
}

export interface ProjectConfig {
  name: string;
  displayName: string;
  apiUrl: string;
  apiEndpoints: {
    auth: string;
    user: string;
    handover: string;
    ai: string;
  };
  logo: string;
  bgLogin: string;
  theme: ThemeTokens;
  branding: BrandingConfig;
}

const functionBaseUrl = 'https://handoverenginev2-7yjyckwx6q-uc.a.run.app';
const unifiedFunctionUrl =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_HANDOVER_ENGINE_URL ||
  functionBaseUrl;
const authServiceUrl = process.env.REACT_APP_AUTH_SERVICE || unifiedFunctionUrl;
const userServiceUrl = process.env.REACT_APP_USER_SERVICE || unifiedFunctionUrl;
const handoverServiceUrl = process.env.REACT_APP_HANDOVER_SERVICE || unifiedFunctionUrl;
const aiServiceUrl = process.env.REACT_APP_AI_SERVICE || unifiedFunctionUrl;

const handoverTheme: ThemeTokens = {
  name: 'Handover Engine',
  colors: {
    primary: {
      main: '#0288d1',
      light: '#5eb8ff',
      dark: '#005b9f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#455a64',
      light: '#718792',
      dark: '#1c313a',
      contrastText: '#ffffff',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ed6c02',
      light: '#ffb74d',
      dark: '#e65100',
      contrastText: '#ffffff',
    },
    info: {
      main: '#0288d1',
      light: '#29b6f6',
      dark: '#01579b',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2e7d32',
      light: '#66bb6a',
      dark: '#1b5e20',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f4f7fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#111827',
      secondary: '#4b5563',
    },
  },
  customPalette: {
    gradients: {
      dark: {
        main: '#0f172a',
        state: '#1e3a5f',
      },
    },
    coloredShadows: {
      primary: 'rgba(2, 136, 209, 0.28)',
      secondary: 'rgba(69, 90, 100, 0.24)',
      info: 'rgba(2, 136, 209, 0.24)',
      success: 'rgba(46, 125, 50, 0.24)',
      warning: 'rgba(237, 108, 2, 0.24)',
      error: 'rgba(211, 47, 47, 0.24)',
      light: 'rgba(148, 163, 184, 0.24)',
      dark: 'rgba(15, 23, 42, 0.3)',
    },
    sidebar: {
      background: '#0f172a',
      backgroundDark: '#0b1220',
      text: '#f8fafc',
      textSecondary: '#94a3b8',
      hover: 'rgba(148, 163, 184, 0.12)',
      active: 'rgba(2, 136, 209, 0.24)',
      border: 'rgba(148, 163, 184, 0.16)',
    },
  },
  spacing: 8,
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
  },
};

const handoverConfig: ProjectConfig = {
  name: 'handover',
  displayName: handoverTheme.name,
  apiUrl: process.env.REACT_APP_API_URL || handoverServiceUrl,
  apiEndpoints: {
    auth: authServiceUrl,
    user: userServiceUrl,
    handover: handoverServiceUrl,
    ai: aiServiceUrl,
  },
  logo,
  bgLogin,
  theme: handoverTheme,
  branding: {
    title: 'Handover Engine',
    tagline: 'Streamline your project handovers',
    description: 'Professional handover management for documentation, access control, and team collaboration.',
    metaDescription:
      'Handover Engine is a project handover platform for documentation, team collaboration, and structured knowledge transfer.',
    companyInfo: {
      legalName: 'Handover Engine Ltd.',
      phone: '(555) 123-4567',
      email: 'info@handoverengine.com',
      address: 'Business Address Here',
    },
    socialLinks: {
      linkedin: 'https://linkedin.com/company/handoverengine',
      twitter: 'https://twitter.com/handoverengine',
    },
    copyright: `Copyright ${new Date().getFullYear()} Handover Engine. All rights reserved.`,
  },
};

export const getProjectConfig = (): ProjectConfig => handoverConfig;

export const projectConfig = getProjectConfig();

export { handoverConfig };
